'use client';
import { useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { TechnicalDetailsTable } from '@/_components/products';
import { contacts, getInTouchDetailsScreen, supportLink } from '@/_config';

/* ─────────────────────────────────────────────────────────────
   Inner panels — reused by both TabView and mobile Dropdown
───────────────────────────────────────────────────────────── */
function TechnicalSpecPanel({ techDetails }) {
    return (
        <div className="py-3">
            <TechnicalDetailsTable data={techDetails} />
        </div>
    );
}

const PRIMARY      = 'var(--primary-color)';
const PRIMARY_BG   = 'rgba(11,49,99,0.05)';
const PRIMARY_BDR  = 'rgba(11,49,99,0.14)';

function WarrantyPanel({ warranty, OutOfWarranty, app_supported, Work_With }) {
    const hasWarranty    = warranty?.duration && Number(warranty.duration) > 0;
    const hasDuration    = hasWarranty ? `${warranty.duration} ${warranty.time}` : null;

    const coverageItems = [
        {
            icon: 'pi-shield',
            label: hasDuration ? `${hasDuration} Warranty` : 'No Warranty',
            sub: hasWarranty ? (warranty.info || 'Manufacturer warranty') : 'Not covered',
            active: !!hasWarranty,
        },
        { icon: 'pi-sync',     label: 'Easy Replacement',  sub: 'Defective product',    active: true },
        { icon: 'pi-truck',    label: 'Transit Safe',       sub: 'Report within 48h',    active: true },
        { icon: 'pi-verified', label: 'Brand Assured',      sub: 'Quality tested',       active: true },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '12px 4px 4px' }}>

            {/* ── Hero warranty badge ─────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '16px', borderRadius: '14px',
                border: `1.5px solid ${hasWarranty ? PRIMARY_BDR : '#e2e8f0'}`,
                background: hasWarranty ? PRIMARY_BG : '#f8fafc',
            }}>
                <div style={{
                    width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
                    background: hasWarranty ? PRIMARY : '#e2e8f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: hasWarranty ? '0 4px 14px rgba(11,49,99,0.22)' : 'none',
                }}>
                    <i className={`pi ${hasWarranty ? 'pi-shield' : 'pi-times-circle'}`}
                        style={{ color: '#fff', fontSize: '1.4rem' }} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: '1.05rem', color: PRIMARY, lineHeight: 1.2 }}>
                        {hasDuration ? `${hasDuration} Manufacturer Warranty` : 'No Warranty'}
                    </div>
                    <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '4px', lineHeight: 1.5 }}>
                        {hasWarranty
                            ? (warranty.info || `Covers manufacturing defects for ${hasDuration}.`)
                            : 'This product does not include a manufacturer warranty.'}
                    </div>
                    {warranty?.title && (
                        <div style={{ fontSize: '0.72rem', color: PRIMARY, marginTop: '5px', fontWeight: 600 }}>
                            {warranty.title}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Coverage chips ──────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {coverageItems.map(({ icon, label, sub, active }) => (
                    <div key={icon} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 12px', borderRadius: '10px',
                        border: `1px solid ${active ? PRIMARY_BDR : '#e2e8f0'}`,
                        background: active ? PRIMARY_BG : '#f8fafc',
                    }}>
                        <i className={`pi ${icon}`} style={{ color: active ? PRIMARY : '#94a3b8', fontSize: '1rem', flexShrink: 0 }} />
                        <div>
                            <div style={{ fontSize: '0.74rem', fontWeight: 700, color: active ? PRIMARY : '#94a3b8', lineHeight: 1.2 }}>
                                {label}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px' }}>{sub}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Out-of-warranty ─────────────────────────────── */}
            {OutOfWarranty && (
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                    padding: '12px 14px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', background: '#f8fafc',
                }}>
                    <i className="pi pi-wrench" style={{ color: '#94a3b8', fontSize: '0.95rem', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '3px' }}>
                            Out-of-Warranty Repair
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.5 }}>{OutOfWarranty.title}</div>
                    </div>
                </div>
            )}

            {/* ── App Supported ───────────────────────────────── */}
            {app_supported?.length > 0 && (
                <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                        App Supported
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {app_supported.map((app, i) => (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: '7px',
                                padding: '7px 12px', borderRadius: '8px',
                                border: `1px solid ${PRIMARY_BDR}`, background: '#fff',
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img src={app?.logo} alt={app?.title} style={{ height: '22px', objectFit: 'contain' }} />
                                <span style={{ fontSize: '0.74rem', fontWeight: 600, color: PRIMARY }}>{app?.title}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Works With ──────────────────────────────────── */}
            {Work_With?.length > 0 && (
                <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                        Works With
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {Work_With.map((app, i) => (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: '7px',
                                padding: '7px 12px', borderRadius: '8px',
                                border: `1px solid ${PRIMARY_BDR}`, background: '#fff',
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img src={app?.logo} alt={app?.title} style={{ height: '20px', objectFit: 'contain' }} />
                                <span style={{ fontSize: '0.74rem', fontWeight: 600, color: PRIMARY }}>{app?.title}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Support ─────────────────────────────────────── */}
            <div style={{
                padding: '12px 14px', borderRadius: '10px',
                border: `1px solid ${PRIMARY_BDR}`, background: PRIMARY_BG,
            }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: PRIMARY, marginBottom: '6px' }}>
                    Need Help?
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '10px', lineHeight: 1.5 }}>
                    {getInTouchDetailsScreen}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                    <a href={`tel:${contacts?.support}`} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        fontSize: '0.78rem', color: PRIMARY, fontWeight: 600,
                        textDecoration: 'none',
                    }}>
                        <i className="pi pi-phone" style={{ fontSize: '0.72rem', color: 'var(--secondary-color)' }} />
                        {contacts?.support}
                    </a>
                    <a href={`mailto:${contacts?.emailSupport}`} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        fontSize: '0.78rem', color: PRIMARY, fontWeight: 600,
                        textDecoration: 'none',
                    }}>
                        <i className="pi pi-envelope" style={{ fontSize: '0.72rem', color: 'var(--secondary-color)' }} />
                        {contacts?.emailSupport}
                    </a>
                </div>
                <a href={supportLink} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: 'var(--primary-color)', color: '#fff',
                    borderRadius: '8px', padding: '7px 14px',
                    fontSize: '0.76rem', fontWeight: 700,
                    textDecoration: 'none',
                }}>
                    <i className="pi pi-headphones" style={{ fontSize: '0.72rem' }} />
                    Contact Support
                    <i className="pi pi-arrow-right" style={{ fontSize: '0.58rem' }} />
                </a>
            </div>

        </div>
    );
}

/* ─────────────────────────────────────────────────────────────
   Tab headers with icon
───────────────────────────────────────────────────────────── */
const SpecHeader = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <i className="pi pi-list" style={{ fontSize: '0.85rem' }} />
        Technical Specification
    </span>
);

const WarrantyHeader = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <i className="pi pi-shield" style={{ fontSize: '0.85rem' }} />
        Product Warranty
    </span>
);

/* ─────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────── */
export function TechSpecsTabs({ productData }) {
    const [activeTab, setActiveTab] = useState(0);

    const panelProps = {
        techDetails:   productData?.techDetails,
        warranty:      productData?.warranty,
        OutOfWarranty: productData?.OutOfWarranty,
        app_supported: productData?.app_supported,
        Work_With:     productData?.Work_With,
    };

    return (
        <TabView
            activeIndex={activeTab}
            onTabChange={(e) => setActiveTab(e.index)}
            pt={{
                navContent: { style: { borderBottom: '2px solid var(--surface-border)' } },
                tab: { style: { flex: 1 } },
                headerAction: { style: { justifyContent: 'center', width: '100%' } },
            }}
        >
            <TabPanel header={SpecHeader}>
                <TechnicalSpecPanel techDetails={panelProps.techDetails} />
            </TabPanel>

            <TabPanel header={WarrantyHeader}>
                <WarrantyPanel
                    warranty={panelProps.warranty}
                    OutOfWarranty={panelProps.OutOfWarranty}
                    app_supported={panelProps.app_supported}
                    Work_With={panelProps.Work_With}
                />
            </TabPanel>
        </TabView>
    );
}
